import React, { memo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { StyHrDataGrid } from 'routes/HumanResourcesModule/components/HrDataGrid/HrDataGrid';
import {
    DateString,
    SimulationResultData,
} from 'routes/HumanResourcesModule/business/types';
import { snakify } from 'helpers/syntaxHelper';
import { useSemiTreatmentOfSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/hooks/semiTreatment';
import CustomResultColumnCellRenderer from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/cellRenderer/CustomResultColumnCellRenderer';
import { DEFAULT_GRID_PROPS } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/gridHelpers';
import { getColDefs } from '../helpers/colDefinitions';
import { getObsoleteGridProps } from '../../Agents/helpers/gridHelpers';
import { TRACKABLE_EVENTS_METADATA } from 'helpers/trackerHelpers';
import HistoricalOrgChartHrContext from 'routes/HumanResourcesModule/Simulation/contexts/ObsoleteOrgChartSimulationContext';

type Props = {
    hrSimulation: HrSimulation;
    resultSimulationData: SimulationResultData[];
    isLoading: boolean;
    cardHeaderActions: React.ReactNode;
    dateColumns: DateString[];
    titleNode: React.ReactNode;
    groupBy: keyof SimulationResultData;
    isMonthly: boolean;
};

const ResultDataGrid = ({
    hrSimulation,
    resultSimulationData,
    isLoading,
    dateColumns,
    cardHeaderActions,
    titleNode,
    isMonthly,
    groupBy,
}: Props): React.ReactElement => {
    const { t } = useTranslation();

    const { data: semiTreatmentHypothesesData = [] } =
        useSemiTreatmentOfSimulation(hrSimulation);
    const { data: obsoleteOrgChartHr } = useContext(
        HistoricalOrgChartHrContext,
    );
    const columnDefs = getColDefs({
        groupBy,
        dateColumns,
        isMonthly,
        semiTreatmentHypothesesData,
        hrSimulation,
    });
    const exportFileName = t(
        'humanResources.simulation.simulationResultDataFileName',
        {
            title: snakify(hrSimulation.title),
        },
    );

    const obsoleteGridProps = getObsoleteGridProps({
        hrSimulation,
        obsoleteOrgChartHr,
    });

    const gridProps = {
        ...obsoleteGridProps,
        components: {
            ...DEFAULT_GRID_PROPS.components,
            customResultColumnCellRenderer: CustomResultColumnCellRenderer,
        },
    };
    return (
        <StyHrDataGrid
            title={t('humanResources.simulation.resultsBy')}
            rowData={resultSimulationData}
            trackEventMetadata={{
                gridName:
                    TRACKABLE_EVENTS_METADATA.human_resources.fullScreen
                        .resultGrid,
            }}
            columnDefs={columnDefs}
            cardHeaderActions={cardHeaderActions}
            exportFileName={exportFileName}
            isLoading={isLoading}
            titleNode={titleNode}
            gridProps={gridProps}
        />
    );
};

export default memo(ResultDataGrid);
